import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Sparkles, ArrowRight, Lightbulb, Heart, Users } from 'lucide-react';
import { useLocaleContext } from '../context/LocaleContext';
import { getTranslation } from '../utils/translations';

// CLS FIX: Removed animateIn state and setTimeout - animations were no-ops causing layout shift
const FeedbackCTA: React.FC = () => {
  const { locale } = useLocaleContext();
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  return (
    <div className="mt-20 max-w-[1400px] mx-auto px-4">
      <div className="py-8 md:py-12">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 px-6 py-3 rounded-full mb-6 border border-purple-200 dark:border-purple-800">
              <div className="p-2 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg text-white">
                <Heart className="w-5 h-5" />
              </div>
              <span className="font-bold text-purple-700 dark:text-purple-300">
                {locale === 'de'
                  ? 'Ihr Feedback ist wichtig'
                  : locale === 'pt-BR'
                  ? 'Sua Opinião Importa'
                  : locale === 'ms-MY'
                  ? 'Maklum Balas Anda Penting'
                  : locale === 'es'
                  ? 'Tu Opinión Importa'
                  : locale === 'fr'
                  ? 'Votre Retour Compte'
                  : locale === 'ja'
                  ? 'ご意見をお聞かせください'
                  : locale === 'id'
                  ? 'Masukan Anda Penting'
                  : 'Your Feedback Matters'}
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-800 bg-clip-text text-transparent leading-tight">
              {locale === 'de'
                ? 'Vermissen Sie etwas? Haben Sie Verbesserungsvorschläge?'
                : locale === 'pt-BR'
                ? 'Falta Algo? Tem Ideias para Melhorias?'
                : locale === 'ms-MY'
                ? 'Ada Yang Kurang? Ada Idea Untuk Penambahbaikan?'
                : locale === 'es'
                ? '¿Falta Algo? ¿Tienes Ideas para Mejorar?'
                : locale === 'fr'
                ? 'Il manque quelque chose ? Des idées d\'amélioration ?'
                : locale === 'ja'
                ? '何か足りないものはありますか？改善のアイデアはありますか？'
                : locale === 'id'
                ? 'Ada yang Kurang? Punya Ide untuk Perbaikan?'
                : 'Missing Something? Have Ideas for Improvement?'}
            </h2>
            
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              {locale === 'de'
                ? 'Wir entwickeln dieses Tool kontinuierlich weiter und Ihre Ideen helfen uns dabei, es noch besser zu machen. Teilen Sie uns mit, welche Features Sie sich wünschen!'
                : locale === 'pt-BR'
                ? 'Estamos constantemente melhorando esta ferramenta e suas ideias nos ajudam a torná-la ainda melhor. Diga-nos quais recursos você gostaria de ver!'
                : locale === 'ms-MY'
                ? 'Kami sentiasa menambah baik alat ini dan idea anda membantu kami menjadikannya lebih baik. Beritahu kami ciri apa yang anda ingin lihat!'
                : locale === 'es'
                ? '¡Estamos mejorando constantemente esta herramienta y tus ideas nos ayudan a hacerla aún mejor. Cuéntanos qué funciones te gustaría ver!'
                : locale === 'fr'
                ? 'Nous améliorons constamment cet outil et vos idées nous aident à le perfectionner. Dites-nous quelles fonctionnalités vous aimeriez voir !'
                : locale === 'ja'
                ? '私たちは常にこのツールを改善しており、皆様のアイデアがより良いものを作る助けになります。どんな機能が欲しいかお聞かせください！'
                : locale === 'id'
                ? 'Kami terus meningkatkan alat ini dan ide Anda membantu kami membuatnya lebih baik. Beritahu kami fitur apa yang ingin Anda lihat!'
                : 'We\'re constantly improving this tool and your ideas help us make it even better. Tell us what features you\'d love to see!'
              }
            </p>
          </div>

          {/* Main CTA Card */}
          <div className="max-w-4xl mx-auto">
            <div
              className="group p-8 bg-gradient-to-br from-white/80 to-purple-50/50 dark:from-gray-800/80 dark:to-purple-900/20 rounded-3xl border-2 border-purple-200 dark:border-purple-800 hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 cursor-pointer relative overflow-hidden"
              onClick={() => navigate('/feature-request')}
              onMouseEnter={() => setHoveredCard('main')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* Hover effect background */}
              <div className="absolute inset-0 bg-gradient-to-r from-purple-50/50 to-pink-50/50 dark:from-purple-900/10 dark:to-pink-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative flex flex-col md:flex-row items-center gap-8">
                {/* Icon and Visual */}
                <div className="flex-shrink-0">
                  <div className={`p-6 bg-gradient-to-r from-purple-500 to-pink-600 rounded-3xl text-white shadow-2xl transition-transform duration-300 ${hoveredCard === 'main' ? 'scale-110 rotate-3' : ''}`}>
                    <MessageSquare className="w-12 h-12" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-grow text-center md:text-left">
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white mb-4 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                    {locale === 'de'
                      ? 'Feature anfragen'
                      : locale === 'pt-BR'
                      ? 'Solicitar um Recurso'
                      : locale === 'ms-MY'
                      ? 'Minta Ciri Baru'
                      : locale === 'es'
                      ? 'Solicitar una Función'
                      : locale === 'fr'
                      ? 'Demander une Fonctionnalité'
                      : locale === 'ja'
                      ? '機能をリクエスト'
                      : locale === 'id'
                      ? 'Minta Fitur Baru'
                      : 'Request a Feature'}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed mb-6">
                    {locale === 'de'
                      ? 'Haben Sie eine Idee für ein neues Feature? Möchten Sie eine Verbesserung vorschlagen? Wir hören zu und setzen die besten Ideen um!'
                      : locale === 'pt-BR'
                      ? 'Tem uma ideia para um novo recurso? Quer sugerir uma melhoria? Nós ouvimos e implementamos as melhores ideias!'
                      : locale === 'ms-MY'
                      ? 'Ada idea untuk ciri baru? Ingin mencadangkan penambahbaikan? Kami mendengar dan melaksanakan idea terbaik!'
                      : locale === 'es'
                      ? '¿Tienes una idea para una nueva función? ¿Quieres sugerir una mejora? ¡Escuchamos e implementamos las mejores ideas!'
                      : locale === 'fr'
                      ? 'Vous avez une idée de nouvelle fonctionnalité ? Vous souhaitez suggérer une amélioration ? Nous écoutons et mettons en oeuvre les meilleures idées !'
                      : locale === 'ja'
                      ? '新機能のアイデアはありますか？改善を提案したいですか？私たちは皆様の声に耳を傾け、優れたアイデアを実現します！'
                      : locale === 'id'
                      ? 'Punya ide untuk fitur baru? Ingin menyarankan perbaikan? Kami mendengarkan dan mewujudkan ide terbaik!'
                      : 'Have an idea for a new feature? Want to suggest an improvement? We listen and implement the best ideas!'
                    }
                  </p>
                  
                  {/* Features List */}
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    {[
                      {
                        icon: Lightbulb,
                        text: locale === 'de'
                          ? 'Neue Feature-Ideen'
                          : locale === 'pt-BR'
                          ? 'Ideias de novos recursos'
                          : locale === 'ms-MY'
                          ? 'Idea ciri baru'
                          : locale === 'es'
                          ? 'Ideas de nuevas funciones'
                          : locale === 'fr'
                          ? 'Idées de nouvelles fonctionnalités'
                          : locale === 'ja'
                          ? '新機能のアイデア'
                          : locale === 'id'
                          ? 'Ide fitur baru'
                          : 'New feature ideas'
                      },
                      {
                        icon: Sparkles,
                        text: locale === 'de'
                          ? 'Verbesserungsvorschläge'
                          : locale === 'pt-BR'
                          ? 'Sugestões de melhoria'
                          : locale === 'ms-MY'
                          ? 'Cadangan penambahbaikan'
                          : locale === 'es'
                          ? 'Sugerencias de mejora'
                          : locale === 'fr'
                          ? 'Suggestions d\'amélioration'
                          : locale === 'ja'
                          ? '改善の提案'
                          : locale === 'id'
                          ? 'Saran perbaikan'
                          : 'Improvement suggestions'
                      },
                      {
                        icon: Users,
                        text: locale === 'de'
                          ? 'Benutzerfreundlichkeit'
                          : locale === 'pt-BR'
                          ? 'Feedback de experiência do usuário'
                          : locale === 'ms-MY'
                          ? 'Maklum balas pengalaman pengguna'
                          : locale === 'es'
                          ? 'Comentarios de experiencia de usuario'
                          : locale === 'fr'
                          ? 'Retours sur l\'expérience utilisateur'
                          : locale === 'ja'
                          ? 'ユーザー体験のフィードバック'
                          : locale === 'id'
                          ? 'Masukan pengalaman pengguna'
                          : 'User experience feedback'
                      },
                      {
                        icon: Heart,
                        text: locale === 'de'
                          ? 'Allgemeines Feedback'
                          : locale === 'pt-BR'
                          ? 'Feedback geral'
                          : locale === 'ms-MY'
                          ? 'Maklum balas umum'
                          : locale === 'es'
                          ? 'Comentarios generales'
                          : locale === 'fr'
                          ? 'Retours généraux'
                          : locale === 'ja'
                          ? '一般的なフィードバック'
                          : locale === 'id'
                          ? 'Masukan umum'
                          : 'General feedback'
                      }
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                          <item.icon size={16} className="text-purple-600 dark:text-purple-400" />
                        </div>
                        <span className="text-gray-700 dark:text-gray-300 font-medium">
                          {item.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA Button */}
                <div className="flex-shrink-0">
                  <div className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-2xl font-bold text-lg transition-all duration-300 transform group-hover:scale-105 shadow-lg group-hover:shadow-xl">
                    <span>
                      {locale === 'de'
                        ? 'Jetzt teilen'
                        : locale === 'pt-BR'
                        ? 'Compartilhar Agora'
                        : locale === 'ms-MY'
                        ? 'Kongsi Sekarang'
                        : locale === 'es'
                        ? 'Compartir Ahora'
                        : locale === 'fr'
                        ? 'Partager Maintenant'
                        : locale === 'ja'
                        ? '今すぐ共有'
                        : locale === 'id'
                        ? 'Bagikan Sekarang'
                        : 'Share Now'}
                    </span>
                    <ArrowRight className={`w-6 h-6 transition-transform duration-300 ${hoveredCard === 'main' ? 'translate-x-1' : ''}`} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Stats */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm px-6 py-3 rounded-full border border-purple-200 dark:border-purple-800 shadow-lg">
              <Sparkles className="w-5 h-5 text-purple-500 animate-pulse" />
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                {locale === 'de'
                  ? 'Jede Idee zählt - gemeinsam machen wir das Tool noch besser!'
                  : locale === 'pt-BR'
                  ? 'Cada ideia conta - juntos tornamos esta ferramenta ainda melhor!'
                  : locale === 'ms-MY'
                  ? 'Setiap idea penting - bersama kita jadikan alat ini lebih baik!'
                  : locale === 'es'
                  ? '¡Cada idea cuenta - juntos hacemos esta herramienta aún mejor!'
                  : locale === 'fr'
                  ? 'Chaque idée compte - ensemble, rendons cet outil encore meilleur !'
                  : locale === 'ja'
                  ? 'すべてのアイデアが大切です - 一緒にこのツールをより良くしましょう！'
                  : locale === 'id'
                  ? 'Setiap ide berarti - bersama kita jadikan alat ini lebih baik!'
                  : 'Every idea counts - together we make this tool even better!'
                }
              </span>
            </div>
          </div>
      </div>
    </div>
  );
};

export default FeedbackCTA;